import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Menu,
  MenuItem,
  Radio,
} from "@mui/material";
import { ArrowDropUp, ArrowDropDown, UnfoldMore } from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import OrderEntry from "../OrderEntry";
import { useSelectedLadderOrdersStore } from "../../Store/selectedLadderOrdersStore";
import { getPreference, setPreference } from "../../Utils/userPreferenceManger";
import { tokens } from "../../Common/designTokens";
import { isStagingOrder } from "../../Constants/stagingBlotter";

const withAlpha = (rgba, alpha) =>
  rgba.replace(/rgba\((\s*\d+\s*,\s*\d+\s*,\s*\d+\s*),\s*[\d.]+\s*\)/, `rgba($1, ${alpha})`);

const BondTable = ({
  selectedColumns,
  filteredData,
  handleRowClick,
  handleViewJson,
  handleBuyClick,
  handleCusipClick,
  onOrderAdded,
  showSelectionColumn = false,
  currentRung = null,
  disabled = false,
  sectionKey = "default",
  onSort,
  sortConfig: externalSortConfig,
  onError, // Add onError prop for error handling
  // NEW: indicate current rung is executed and we should show the message overlay
  isExecutedRung = false,
}) => {
  // State and refs
  const [columns, setColumns] = useState(selectedColumns);
  // Use external sort config if provided, otherwise use local state
  const [localSortConfig, setLocalSortConfig] = useState({ key: null, direction: "asc" });
  const sortConfig = externalSortConfig || localSortConfig;
  const [columnWidths, setColumnWidths] = useState({});
  const tableRef = useRef(null);
  const tableContainerRef = useRef(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [orderEntryOpen, setOrderEntryOpen] = useState(false);
  const {
    selectedOrders,
  } = useSelectedLadderOrdersStore();
  const userRole = localStorage.getItem("role");

  const getValueColor = (columnKey, isBuy, isSell) => {
    if (isSell) return tokens.colors.sellFontColor;
    if (isBuy) return tokens.colors.buyFontColor;
    if (["totalGL", "realizedGL", "unrealizedGL"].includes(columnKey)) {
      return tokens.colors.buyFontColor;
    }
    return tokens.colors.fontColour;
  };

  // Resizing refs
  const resizingColumnRef = useRef(null);
  const initialWidthRef = useRef(0);
  const initialXRef = useRef(0);

  // Get access to the selected orders store
  const { toggleOrder, isSelected } = useSelectedLadderOrdersStore();

  // Helper function to create a unique ID for each row
  const getRowUniqueId = (rowData) => {
    if (!rowData?.bondInventoryId) return "";
    return rowData.bondInventoryId.toString();
  };

  // Load saved column widths on initial render using role-based preferences
  useEffect(() => {
    try {
      const savedWidths = getPreference(`columnWidths_${sectionKey}`);
      if (savedWidths) {
        const parsedWidths = savedWidths;
        setColumnWidths(parsedWidths);
      }
    } catch (e) {
      console.error("Error loading saved column widths:", e);
    }
  }, [sectionKey]);

  // Add new effect to save column widths whenever they change using role-based preferences
  useEffect(() => {
    // Only save if we have widths to save and after initial load
    if (Object.keys(columnWidths).length > 0) {
      try {
        setPreference(`columnWidths_${sectionKey}`, columnWidths);
      } catch (e) {
        console.error("Error saving column widths:", e);
      }
    }
  }, [columnWidths, sectionKey]);

  const calculateMaxContentWidth = (columnKey) => {
    // Check if we have a saved width for this column using role-based preferences
    const savedWidth = columnWidths[columnKey];
    if (savedWidth) {
      return savedWidth;
    }

    const span = document.createElement("span");
    span.style.visibility = "hidden";
    span.style.position = "absolute";
    span.style.whiteSpace = "nowrap";
    document.body.appendChild(span);

    span.textContent = selectedColumns.find((col) => col.key === columnKey)?.name || "";
    let maxWidth = span.offsetWidth;

    filteredData.forEach((row) => {
      const value = formatNumber(row[columnKey], columnKey);
      span.textContent = value?.toString() || "";
      maxWidth = Math.max(maxWidth, span.offsetWidth);
    });

    document.body.removeChild(span);
    return Math.min(maxWidth + 50, 100);
  };

  useEffect(() => {
    setColumns(selectedColumns);
    setColumnWidths((prev) => {
      const newWidths = { ...prev };
      selectedColumns.forEach((col) => {
        if (!(col.key in newWidths)) {
          newWidths[col.key] = calculateMaxContentWidth(col.key);
        }
      });
      Object.keys(newWidths).forEach((key) => {
        if (!selectedColumns.some((col) => col.key === key)) {
          delete newWidths[key];
        }
      });
      return newWidths;
    });
  }, [selectedColumns, filteredData]);

  // Add this useEffect to reset scroll
  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [filteredData]);

  // Add a useEffect to reset the sorted data when filteredData changes
  useEffect(() => {
    if (!externalSortConfig) {
      setLocalSortConfig({ key: null, direction: "asc" });
    }
  }, [filteredData, externalSortConfig]);

  // Update the sortedData calculation to handle empty or loading states
  
  const handleSort = (columnKey) => {
    const newDirection =
      sortConfig?.key === columnKey && sortConfig?.direction === "asc"
        ? "desc"
        : "asc";
    onSort(columnKey, newDirection);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const reorderedColumns = [...columns];
    const [movedColumn] = reorderedColumns.splice(result.source.index, 1);
    reorderedColumns.splice(result.destination.index, 0, movedColumn);
    setColumns(reorderedColumns);

    // Save using role-based preferences
    const cleanedSectionKey = sectionKey && sectionKey.includes('=') 
      ? sectionKey.split('=').pop() 
      : sectionKey;
    
    const effectiveSectionKey = cleanedSectionKey || sectionKey;

    if (effectiveSectionKey) {
      // Save current visible column order
      setPreference(`columnOrder_${effectiveSectionKey}`, reorderedColumns);

      // Update full column order while maintaining invisible columns
      const savedFullOrder = getPreference(`fullColumnOrder_${effectiveSectionKey}`);
      if (savedFullOrder) {
        try {
          const fullOrderedColumns = savedFullOrder;
          
          // Create a map of visible column positions
          const newPositions = {};
          reorderedColumns.forEach((col, idx) => {
            newPositions[col.key] = idx;
          });
          
          // Create sets for quick lookup
          const visibleKeys = new Set(reorderedColumns.map(col => col.key));
          
          // Reorder visible columns and keep invisible ones in their relative positions
          const newFullOrder = [];
          
          // First add all visible columns in their new order
          reorderedColumns.forEach(col => {
            newFullOrder.push(col);
          });
          
          // Then add all invisible columns in their original order
          fullOrderedColumns.forEach(col => {
            if (!visibleKeys.has(col.key)) {
              newFullOrder.push(col);
            }
          });
          
          // Save the updated full order
          setPreference(`fullColumnOrder_${effectiveSectionKey}`, newFullOrder);
          
          // Also update selected columns to match current order
          const savedSelectedColumns = getPreference(`selectedColumns_${effectiveSectionKey}`);
          if (savedSelectedColumns) {
            const selectedColumns = savedSelectedColumns;
            const reorderedSelected = reorderedColumns.filter(col => 
              selectedColumns.some(selected => selected.key === col.key)
            );
            setPreference(`selectedColumns_${effectiveSectionKey}`, reorderedSelected);
          }
        } catch (e) {
          console.error("Error updating full column order:", e);
        }
      }
    }
  };

  const startResizing = (e, columnKey) => {
    e.stopPropagation();
    resizingColumnRef.current = columnKey;
    initialWidthRef.current = columnWidths[columnKey] || 150;
    initialXRef.current = e.clientX;
    document.addEventListener("mousemove", handleResizing);
    document.addEventListener("mouseup", stopResizing);
    document.body.style.cursor = "col-resize";
  };

  const handleResizing = (e) => {
    if (resizingColumnRef.current) {
      const delta = e.clientX - initialXRef.current;
      const newWidth = initialWidthRef.current + delta;
      const minWidth = 50;
      const maxWidth = 500;
      const clampedWidth = Math.max(minWidth, Math.min(newWidth, maxWidth));
      setColumnWidths((prev) => ({
        ...prev,
        [resizingColumnRef.current]: clampedWidth,
      }));
    }
  };

  const stopResizing = () => {
    resizingColumnRef.current = null;
    document.removeEventListener("mousemove", handleResizing);
    document.removeEventListener("mouseup", stopResizing);
    document.body.style.cursor = "";
  };

  const formatNumber = (value, columnKey, rowData) => {
    if (value === undefined || value === null || value === "") return "---";

    // Helper function to format dates as DDMonYY with optional time
    const formatDateToDDMonYY = (dateStr, showTime = true) => {
      try {
        // Ensure timestamp is treated as UTC by appending 'Z' if not present
        let valueStr = dateStr.toString();
        if (!valueStr.endsWith('Z')) {
          valueStr += 'Z';
        }
        
        const date = new Date(valueStr);
        if (isNaN(date.getTime())) return "---";
        
        const day = date.getUTCDate();
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getUTCFullYear().toString().slice(-2);
        
        // Add time in 24-hour format only if showTime is true
        if (showTime) {
          const hours = date.getUTCHours().toString().padStart(2, '0');
          const minutes = date.getUTCMinutes().toString().padStart(2, '0');
          return `${day}${month}${year} ${hours}:${minutes}`;
        }
        
        // Return only the date portion
        return `${day}${month}${year}`;
      } catch (e) {
        console.error("Error formatting date:", e, "for value:", dateStr);
        return "---";
      }
    };

    // New helper to format maturity as MM/DD/YYYY
    const formatDateToMMDDYYYY = (dateStr) => {
      try {
        let valueStr = dateStr.toString();
        if (!valueStr.endsWith('Z')) {
          valueStr += 'Z';
        }
        const date = new Date(valueStr);
        if (isNaN(date.getTime())) return "---";
        const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
        const dd = String(date.getUTCDate()).padStart(2, '0');
        const yyyy = date.getUTCFullYear();
        return `${mm}/${dd}/${yyyy}`;
      } catch (e) {
        console.error("Error formatting date to MM/DD/YYYY:", e, "for value:", dateStr);
        return "---";
      }
    };

    if (columnKey === "insured" || columnKey === "altMinTax") {
      return value === 1 ? "Yes" : value === 0 ? "No" : "---";
    }

    const numericColumns = [
      "qty",
      "quantity",
      "price",
      "minQty",
      "maxQty",
      "ytm",
      "ytw1",
      "accruedInterest",
      "estimatedTotal",
      "duration",
      "executedQuantity",
      "executedAveragePrice",
      "numberOfRungs",
      "amountPerRung",
      "totalMaturity",
      "poolFactor",
    ];  // Removed coupon from this array
    if (numericColumns.includes(columnKey)) {
      if (typeof value === "number" || !isNaN(Number(value))) {
        const num = Number(value);
        const twoDecimalPlaces = [
          "price",
          "accruedInterest",
          "estimatedTotal",
          "executedAveragePrice",
          "poolFactor",
        ];  // Removed coupon from this array
        const decimalPlaces = twoDecimalPlaces.includes(columnKey) ? 2 : 0;
        return num.toLocaleString("en-US", {
          minimumFractionDigits: decimalPlaces,
          maximumFractionDigits: decimalPlaces,
        });
      }
      return "---";
    }
    const dateColumns = ["maturity", "nextCallDate", "createdAt", "dateTime"];
    if (dateColumns.includes(columnKey) && value) {
      // For maturity column, don't show time - render as MM/DD/YYYY
      if (columnKey === "maturity" ||columnKey === "nextCallDate") {
        return formatDateToMMDDYYYY(value);
      }
      return formatDateToDDMonYY(value);
    }
    if (columnKey === "coupon") {
      return value !== null && value !== undefined ? value : "---";
    }
    return value;
  };

  const isNumericOrDate = (value, columnKey) => {
    const numericColumns = [
      "qty",
      "quantity",
      "price",
      "minQty",
      "maxQty",
      "ytm",
      "ytw1",
      "accruedInterest",
      "estimatedTotal",
      "duration",
      "executedQuantity",
      "executedAveragePrice",
      "numberOfRungs",
      "amountPerRung",
      "totalMaturity",
      "altMinTax",
      "insured",
      "ytc",
      "coupon", // Add coupon here to keep it right-aligned
      "poolFactor",
    ];
    const dateColumns = ["maturity", "nextCallDate", "createdAt", "dateTime"];
    return numericColumns.includes(columnKey) || dateColumns.includes(columnKey);
  };

  const isBuySellColumn = (columnKey) => {
    return columnKey === "side" || columnKey === "action";
  };

  const isTypeLikeColumn = (columnKey) => {
    return columnKey === "type" || columnKey === "bondType" || columnKey === "bondSubType";
  };

  const handleContextMenu = (event, rowData) => {
    event.preventDefault();
    if (isStagingOrder(rowData) && userRole === "Trader") {
      setSelectedRowData(rowData);
      setContextMenu(
        contextMenu === null
          ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 }
          : null
      );
    }
  };

  const handleCloseContextMenu = () => setContextMenu(null);

  const handleSendToMarket = () => {
    handleCloseContextMenu();
    setOrderEntryOpen(true);
  };

  const rowHeight = "40px";
  const cellPadding = "4px 8px";

  return (
    <Box
      sx={{
        px: "16px",
        py: "0px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      <TableContainer
        component={Paper}
        ref={tableContainerRef}
        sx={{
          height: "100%",
          width: "100%",
          borderRadius: "0px", // CHANGED (was 0px)
          overflow: "auto",
          position: "relative",
          backgroundColor: tokens.colors.defaultGridRowColor, // CHANGED
          maxHeight: "100%",
          "& .sticky-column": {
            position: "sticky",
            left: 0,
            backgroundColor: tokens.colors.defaultGridRowColor, // CHANGED
            zIndex: 2,
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "inherit",
              zIndex: -1,
            },
          },
          "& .sticky-header": {
            position: "sticky",
            left: 0,
            top: 0,
            backgroundColor: tokens.colors.defaultGridHeaderBackground, // CHANGED
            zIndex: 3,
          },
          "& .vertical-sticky-header": {
            position: "sticky",
            top: 0,
            backgroundColor: tokens.colors.defaultGridHeaderBackground, // CHANGED
            zIndex: 2,
          },
          "& .MuiTableCell-root": {
            borderBottom: `1px solid ${tokens.colors.defaultGridBorderColor}`, // NEW
          },
        }}
      >
        <DragDropContext onDragEnd={handleDragEnd}>
          <Table stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <Droppable droppableId="columns" direction="horizontal" type="column">
                {(provided) => (
                  <TableRow ref={provided.innerRef} {...provided.droppableProps}>
                    {showSelectionColumn && (
                      <TableCell
                        sx={{
                          backgroundColor: tokens.colors.defaultGridHeaderBackground, // CHANGED
                          color: tokens.colors.headerFontColour, // CHANGED
                          padding: cellPadding,
                          fontWeight: 700,
                          height: rowHeight,
                          userSelect: "none",
                          borderBottom: `2px solid ${tokens.colors.defaultGridBorderColor}`, // CHANGED
                          width: "60px",
                          position: "sticky",
                          left: 0,
                          top: 0,
                          zIndex: 5,
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: tokens.colors.defaultGridHeaderBackground, // CHANGED
                            zIndex: -1,
                          }
                        }}
                      >
                        Select
                      </TableCell>
                    )}
                    {columns.map((column, index) => (
                      <Draggable
                        key={column.key}
                        draggableId={column.key}
                        index={index}
                      >
                        {(provided) => (
                          <TableCell
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            sx={{
                              backgroundColor: tokens.colors.defaultGridHeaderBackground, // CHANGED
                              color: tokens.colors.headerFontColour, // CHANGED
                              padding: cellPadding,
                              fontWeight: 700,
                              height: rowHeight,
                              userSelect: "none",
                              borderBottom: `2px solid ${tokens.colors.defaultGridBorderColor}`, // CHANGED
                              position: "relative",
                              overflow: "hidden",
                              width: columnWidths[column.key] || 150,
                            }}
                            className={
                              index === 0 && !showSelectionColumn
                                ? "sticky-header"
                                : "vertical-sticky-header"
                            }
                          >
                            {/* Full-width drag handle wrapper */}
                            <div
                              {...provided.dragHandleProps}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: column.isNumeric ? "flex-end" : "flex-start",
                                height: "100%",
                                width: "100%",
                                paddingRight: "5px",
                                boxSizing: "border-box",
                                cursor: "grab",
                              }}
                            >
                              <span
                                style={{
                                  display: "block",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  marginRight: "4px",
                                }}
                                title={column.name}
                              >
                                {column.name}
                              </span>
                              <span
                                onClick={() => handleSort(column.key)}
                                style={{
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {sortConfig.key === column.key ? (
                                  sortConfig.direction === "asc" ? (
                                    <ArrowDropUp fontSize="small" />
                                  ) : (
                                    <ArrowDropDown fontSize="small" />
                                  )
                                ) : (
                                  <UnfoldMore
                                    fontSize="small"
                                    sx={{ opacity: 0.5, "&:hover": { opacity: 1 } }}
                                  />
                                )}
                              </span>
                            </div>
                            <div
                                style={{
                                  position: "absolute",
                                  right: 0,
                                  top: 0,
                                  bottom: 0,
                                  width: "5px",
                                  backgroundColor: tokens.colors.bodyBorderColor50, // CHANGED
                                  cursor: "col-resize",
                                }}
                                onMouseDown={(e) => startResizing(e, column.key)}
                            />
                          </TableCell>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </TableRow>
                )}
              </Droppable>
            </TableHead>
            <TableBody>
              {filteredData.map((rowData, rowIndex) => {
                // Generate a unique identifier for this row using bondInventoryId
                const rowUniqueId = getRowUniqueId(rowData);

                // Check if this row is selected for the current rung
                const isRowSelected = isSelected(currentRung, rowData.bondInventoryId);

                return (
                  <TableRow
                    key={rowUniqueId}
                    onContextMenu={(e) => handleContextMenu(e, rowData)}
                    onClick={() => handleRowClick(rowIndex)}
                    sx={{
                      cursor: "pointer",
                      height: rowHeight,
                      "&:hover": { backgroundColor: tokens.colors.defaultGridHoverColor }, // CHANGED
                      backgroundColor: tokens.colors.defaultGridRowColor, // CHANGED
                    }}
                  >
                    {showSelectionColumn && (
                      <TableCell
                        padding="checkbox"
                        sx={{
                          height: rowHeight,
                          padding: cellPadding,
                          textAlign: "center",
                          width: "60px",
                          position: "sticky",
                          left: 0,
                          zIndex: 2,
                          backgroundColor: tokens.colors.defaultGridRowColor, // CHANGED
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Radio
                          checked={isRowSelected}
                          value={rowUniqueId}
                          disabled={selectedOrders[currentRung] && selectedOrders[currentRung].isEditable === false}
                          onChange={() => {
                            // Check if current order is not editable
                            if (selectedOrders[currentRung] && selectedOrders[currentRung].isEditable === false) {
                              return; // Prevent toggle
                            }
                            console.log('Toggling order with data:', {
                              rung: currentRung,
                              bondInventoryId: rowData.bondInventoryId,
                              rowData
                            });
                            toggleOrder(currentRung, rowData);
                          }}
                          name={`rung-${currentRung}-selection`}
                          sx={{
                            color: tokens.colors.fontColour,
                            "&.Mui-checked": { color: tokens.palette.primary},
                            "&.Mui-disabled": { 
                              color: withAlpha(tokens.colors.fontColour, 0.3),
                              "&.Mui-checked": { color: withAlpha(tokens.palette.primary, 0.3)}
                            }
                          }}
                        />
                      </TableCell>
                    )}
                    {columns.map((column, colIndex) => {
                      const isNumOrDate = isNumericOrDate(rowData[column.key], column.key);
                      const isBuy = rowData[column.key] === "BUY";
                      const isSell = rowData[column.key] === "SELL";
                      const formattedValue = formatNumber(rowData[column.key], column.key);
                      const valueColor = getValueColor(column.key, isBuy, isSell); // NEW

                      return (
                        <TableCell
                          key={column.key}
                          sx={{
                            height: rowHeight,
                            padding: cellPadding,
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            textAlign: isBuySellColumn(column.key)
                              ? "center"
                              : isTypeLikeColumn(column.key)
                              ? "left"
                              : isNumOrDate
                              ? "right"
                              : "left",
                            backgroundColor: tokens.colors.defaultGridRowColor,
                            color: valueColor, // CHANGED (dynamic buy/sell/normal)
                            fontWeight: isSell || isBuy ? 700 : 400,
                            width: columnWidths[column.key] || 150,
                            position: "relative",
                            isolation: "isolate",
                          }}
                          className={
                            colIndex === 0 && !showSelectionColumn
                              ? "sticky-column"
                              : ""
                          }
                          onClick={(e) => {
                            if (column.key === "cusip") {
                              e.stopPropagation();
                              handleCusipClick(rowIndex, rowData);
                            }
                          }}
                        >
                          {/* BUY/SELL background tint */}
                          {(isBuy || isSell) && (
                            <Box
                              sx={{
                                position: "absolute",
                                inset: 0,
                                zIndex: 1,
                              }}
                            />
                          )}
                          {column.render ? (
                            <Box sx={{ position: "relative", zIndex: 2, color: valueColor }}>
                              {column.render(rowData[column.key], rowData, handleViewJson)}
                            </Box>
                          ) : (
                            <span
                              style={{
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                textAlign: isBuySellColumn(column.key)
                                  ? "center"
                                  : isTypeLikeColumn(column.key)
                                  ? "left"
                                  : isNumOrDate
                                  ? "right"
                                  : "left",
                                lineHeight: rowHeight,
                                position: "relative",
                                zIndex: 2,
                                textDecoration:
                                  (isBuy || isSell) && column.key === "side"
                                    ? "underline"
                                    : "none",
                                color: valueColor, // NEW ensure span inherits correct color
                                fontWeight: isSell || isBuy ? 700 : 400,
                              }}
                              title={formattedValue}
                              onClick={(e) => {
                                if (column.key === "cusip") {
                                  e.stopPropagation();
                                  handleCusipClick(rowIndex, rowData);
                                }
                              }}
                            >
                              {formattedValue}
                            </span>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </DragDropContext>

        {/* Executed rung message overlay */}
        {isExecutedRung && filteredData.length === 1 && (
          <Box
            sx={{
              position: "absolute",
              top: "60%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              pointerEvents: "none",
              px: 2,
              zIndex: 20,
            }}
          >
            <Box
              sx={{
                fontSize: "20px",
                fontWeight: 400,
                color: "rgba(255, 255, 255, 0.6)",
                textAlign: "center",
                whiteSpace: "pre-line",
                width: "100%",
              }}
            >
              {`An order has already been executed for this rung.\nYou are not allowed to select any more bond.`}
            </Box>
          </Box>
        )}
      </TableContainer>

      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleSendToMarket}>Send to market</MenuItem>
      </Menu>

      {orderEntryOpen && selectedRowData && (
        <OrderEntry
          open={orderEntryOpen}
          onClose={() => setOrderEntryOpen(false)}
          source="sendToMarket"
          initialData={selectedRowData}
          readOnly={false}
          token={localStorage.getItem("token")}
          onOrderAdded={onOrderAdded}
          onError={onError} // Pass error handler
        />
      )}
    </Box>
  );
};

export default BondTable;
